import { useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { archiveChannel, listChannels, type Channel } from '../api/channels.js';
import { ApiError } from '../api/apiClient.js';
import { ChannelIcon, iconKindFromChannelType } from '../components/ChannelIcon.js';
import { ConfirmModal } from '../components/ConfirmModal.js';
import { StatusBadge } from '../components/StatusBadge.js';
import { useUiStore } from '../state/uiStore.js';
import { t } from '../i18n/es.js';

export function ChannelsList(): JSX.Element {
  const { id = '' } = useParams<{ id: string }>();
  const [includeArchived, setIncludeArchived] = useState(false);
  const [confirmTarget, setConfirmTarget] = useState<Channel | null>(null);
  const pushToast = useUiStore((s) => s.pushToast);
  const setActiveTenant = useUiStore((s) => s.setActiveTenant);
  const activeTenantId = useUiStore((s) => s.activeTenantId);
  const queryClient = useQueryClient();

  useEffect(() => {
    if (id && id !== activeTenantId) setActiveTenant(id);
  }, [id, activeTenantId, setActiveTenant]);

  const { data, isLoading, error } = useQuery({
    queryKey: ['channels', id, { includeArchived }],
    queryFn: () => listChannels(id, { includeArchived }),
    enabled: Boolean(id),
  });

  const archiveMutation = useMutation({
    mutationFn: (channelId: string) => archiveChannel(id, channelId),
    onSuccess: () => {
      pushToast('success', t.channel.archived);
      queryClient.invalidateQueries({ queryKey: ['channels', id] });
      setConfirmTarget(null);
    },
    onError: (e: unknown) => {
      const msg = e instanceof ApiError ? e.message : (e as Error).message;
      pushToast('error', msg);
    },
  });

  const channels = data ?? [];

  return (
    <div>
      <div className="mb-4 flex items-center justify-between">
        <h2 className="text-xl font-semibold">{t.channel.listTitle}</h2>
        <div className="flex items-center gap-3">
          <label className="flex items-center gap-1 text-sm text-slate-600">
            <input
              type="checkbox"
              checked={includeArchived}
              onChange={(e) => setIncludeArchived(e.target.checked)}
            />
            {t.channel.showArchived}
          </label>
          <Link
            to={`/tenants/${id}/channels/new`}
            className="rounded bg-brand-500 px-3 py-1 text-sm text-white hover:bg-brand-600"
          >
            {t.channel.newBtn}
          </Link>
        </div>
      </div>

      {isLoading && <p className="text-slate-500">{t.app.loading}</p>}
      {error && <p className="text-red-700">{(error as Error).message}</p>}
      {!isLoading && !error && channels.length === 0 && (
        <p className="text-slate-500">{t.channel.empty}</p>
      )}

      {channels.length > 0 && (
        <div className="overflow-x-auto rounded border border-slate-200 bg-white">
          <table className="w-full text-sm">
            <thead className="bg-slate-50 text-left text-xs uppercase text-slate-500">
              <tr>
                <th className="px-4 py-2">{t.channel.fields.name}</th>
                <th className="px-4 py-2">{t.channel.fields.channelType}</th>
                <th className="px-4 py-2">{t.channel.fields.status}</th>
                <th className="px-4 py-2">{t.channel.fields.displayPhoneNumber} / {t.channel.fields.domain}</th>
                <th className="px-4 py-2">{t.channel.fields.version}</th>
                <th className="px-4 py-2 text-right">{t.channel.fields.actions}</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {channels.map((ch) => (
                <tr
                  key={ch.id}
                  data-testid={`channel-row-${ch.id}`}
                  className={ch.deletedAt ? 'opacity-50' : undefined}
                >
                  <td className="px-4 py-2 font-medium">
                    <span className="inline-flex items-center gap-2">
                      <ChannelIcon kind={iconKindFromChannelType(ch.channelType)} size={20} />
                      {ch.name}
                    </span>
                  </td>
                  <td className="px-4 py-2 font-mono text-xs">{ch.channelType}</td>
                  <td className="px-4 py-2">
                    <StatusBadge status={ch.status} />
                  </td>
                  <td className="px-4 py-2 text-slate-600">
                    {ch.displayPhoneNumber ?? ch.domain ?? '—'}
                  </td>
                  <td className="px-4 py-2 text-slate-500">v{ch.version}</td>
                  <td className="px-4 py-2 text-right">
                    {!ch.deletedAt && (
                      <div className="flex justify-end gap-2">
                        <Link
                          to={`/tenants/${id}/channels/${ch.id}/edit`}
                          className="rounded border border-slate-300 px-2 py-1 text-xs hover:bg-slate-50"
                        >
                          {t.channel.edit}
                        </Link>
                        <button
                          type="button"
                          onClick={() => setConfirmTarget(ch)}
                          className="rounded border border-red-300 px-2 py-1 text-xs text-red-700 hover:bg-red-50"
                        >
                          {t.channel.archive}
                        </button>
                      </div>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <ConfirmModal
        open={confirmTarget !== null}
        title={t.channel.confirmArchiveTitle}
        description={t.channel.confirmArchiveDesc}
        confirmLabel={t.channel.archive}
        cancelLabel={t.app.cancel}
        loading={archiveMutation.isPending}
        onConfirm={() => {
          if (confirmTarget) archiveMutation.mutate(confirmTarget.id);
        }}
        onCancel={() => setConfirmTarget(null)}
      />
    </div>
  );
}
